import React from 'react';
import { Box } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';

interface MarkdownRendererProps {
  content: string;
  variant?: 'body1' | 'body2' | 'caption';
  color?: string;
  maxLines?: number;
  sx?: object;
}

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({
  content,
  variant = 'body2',
  color = 'text.secondary',
  maxLines,
  sx = {}
}) => {
  if (!content) {
    return null;
  }

  const baseStyles = {
    '& h1, & h2, & h3, & h4, & h5, & h6': {
      marginTop: 1,
      marginBottom: 0.5,
      fontWeight: 'bold',
    },
    '& h1': { fontSize: '1.5rem' },
    '& h2': { fontSize: '1.25rem' },
    '& h3': { fontSize: '1.1rem' },
    '& h4, & h5, & h6': { fontSize: '1rem' },
    '& p': {
      marginBottom: 0.5,
      lineHeight: 1.5,
    },
    '& ul, & ol': {
      paddingLeft: 2,
      marginBottom: 0.5,
      marginTop: 0,
    },
    '& li': {
      marginBottom: 0.25,
    },
    '& blockquote': {
      borderLeft: 3,
      borderColor: 'primary.main',
      paddingLeft: 1,
      margin: '0.5rem 0',
      fontStyle: 'italic',
      backgroundColor: 'grey.50',
      padding: 1,
      borderRadius: 0.5,
    },
    '& pre': {
      backgroundColor: 'grey.100',
      padding: 1,
      borderRadius: 1,
      overflow: 'auto',
      fontSize: '0.875rem',
    },
    '& code': {
      backgroundColor: 'grey.100',
      padding: '2px 4px',
      borderRadius: 0.5,
      fontSize: '0.875rem',
      fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
    },
    '& pre code': {
      backgroundColor: 'transparent',
      padding: 0,
    },
    '& img': {
      maxWidth: '100%',
      height: 'auto',
      borderRadius: 1,
    },
    '& table': {
      width: '100%',
      borderCollapse: 'collapse',
      marginBottom: 1,
      marginTop: 0.5,
    },
    '& th, & td': {
      border: 1,
      borderColor: 'divider',
      padding: 0.5,
      textAlign: 'left',
    },
    '& th': {
      backgroundColor: 'grey.100',
      fontWeight: 'bold',
    },
    '& a': {
      color: 'primary.main',
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    '& strong': {
      fontWeight: 'bold',
    },
    '& em': {
      fontStyle: 'italic',
    },
    // Apply line clamping if maxLines is specified
    ...(maxLines && {
      display: '-webkit-box',
      WebkitLineClamp: maxLines,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
    }),
    ...sx,
  };

  return (
    <Box
      sx={baseStyles}
      component="div"
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeSanitize]}
      >
        {content}
      </ReactMarkdown>
    </Box>
  );
};

export default MarkdownRenderer; 